'use client';

import { Button } from '../ui/Button';
import Icon from '../ui/Icon';
import { Option } from '../ui/CustomSelect';
import EditableText from '../ui/EditableText';
import Pill, { PillProps } from '../ui/Pill';
import Link from 'next/link';
import { cn, formatNumber, getErrorMessage } from '@/app/lib/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import TimeRangePicker from '../ui/TimeRangePicker';
import Checkbox from '../ui/Checkbox';
import Switch from '../ui/Switch';
import { useTranslations } from 'next-intl';

export type InfoTableItem = {
  name: string;
  labelSuffix?: React.ReactNode;
  showLabel?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  description?: string;
  value:
    | string
    | React.ReactNode
    | string[]
    | { min: number; max: number }
    | { start?: string; end?: string }
    | boolean;
  isEditable?: boolean;
  isInlineEdit?: boolean;
  type?:
    | 'text'
    | 'link'
    | 'image'
    | 'tags'
    | 'select'
    | 'date'
    | 'textarea'
    | 'number'
    | 'range'
    | 'user-select'
    | 'checkbox'
    | 'switch';
  subType?: 'date' | 'datetime' | 'text' | 'number' | 'time';
  key?: string;
  shouldFormat?: boolean;
  prefixIcon?: React.ReactNode;
  inputClassName?: string;
  prefixWrapperClassName?: string;
  editType?: string;
  options?: Option[];
  searchable?: boolean;
  multiple?: boolean;
  maxTags?: number;
  validation?: string[];
  characterLimit?: number;
  placeholder?: string;
  rangeKeys?: {
    start?: string;
    end?: string;
    min?: string;
    max?: string;
  };
  itemWrapperClassName?: string;
  showBadge?: boolean;
  badgeLabel?: string;
  badgeProps?: PillProps;
  columnSpan?: 1 | 2;
};

type InfoTableProps = {
  data: InfoTableItem[];
  onEdit?: (data: InfoTableItem) => void;
  onInlineEdit?: (key: string, value: any) => void | Promise<string>;
  onFieldAdd?: (item: InfoTableItem) => void;
  validation?: string[];
  columns?: 1 | 2;
};

type Progress = Record<
  string,
  {
    status: 'pending' | 'success' | 'error';
    message: string;
  }
>;

const TimeRangeControl = ({
  item,
  initialEditing,
  onEmptyChange,
  onSave,
}: {
  item: InfoTableItem;
  initialEditing?: boolean;
  onEmptyChange: (isEditing: boolean, value: { start?: string; end?: string }) => void;
  onSave: (values: Record<string, string>) => void;
}) => {
  const value = (item.value as { start?: string; end?: string }) || {};
  return (
    <TimeRangePicker
      value={value}
      initialOpen={initialEditing}
      onClose={() => onEmptyChange(false, value)}
      onApply={range => {
        const startKey = item.rangeKeys?.start?.split('.').pop() || 'start_time';
        const endKey = item.rangeKeys?.end?.split('.').pop() || 'end_time';
        onSave({ [startKey]: range.start, [endKey]: range.end });
      }}
    />
  );
};
const isEmptyValue = (value: InfoTableItem['value']) =>
  value === undefined ||
  value === null ||
  value === '' ||
  value === '-' ||
  value === '- - -' ||
  value === 0 ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    (Object.keys(value).length === 0 ||
      ('start' in (value as any) && !(value as any).start && !(value as any).end) ||
      ('min' in (value as any) && !(value as any).min && !(value as any).max)));

const CopyButton = ({ value }: { value: string }) => {
  const t = useTranslations('common');
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy text: ', error);
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      title={isCopied ? t('copied') : t('copy')}
      className={cn(
        'size-4 p-0 text-muted-foreground hover:bg-transparent hover:text-foreground [&_svg]:size-3.5 transition-opacity',
        isCopied
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none group-hover:pointer-events-auto group-hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100'
      )}
      onClick={handleCopy}
    >
      {isCopied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </Button>
  );
};

const getCopyableValue = (item: InfoTableItem): string | null => {
  if (isEmptyValue(item.value)) {
    return null;
  }

  if (typeof item.value === 'boolean') {
    return null;
  }

  if (item.type === 'checkbox' || item.type === 'switch' || item.type === 'image') {
    return null;
  }

  if (item.type === 'tags' || Array.isArray(item.value)) {
    if (Array.isArray(item.value)) {
      const tags = item.value.map(v => String(v).trim()).filter(Boolean);
      return tags.length > 0 ? tags.join(', ') : null;
    }
    if (typeof item.value === 'string') {
      const trimmed = item.value.trim();
      return trimmed && trimmed !== '-' && trimmed !== '- - -' ? trimmed : null;
    }
  }

  if (item.type === 'select' || item.type === 'user-select') {
    if (Array.isArray(item.value)) {
      const labels = item.value
        .map(id => {
          const opt = item.options?.find(o => o.id === id);
          return opt ? String(opt.value) : String(id);
        })
        .filter(Boolean);
      return labels.length > 0 ? labels.join(', ') : null;
    }
    const selectedOption = item.options?.find(o => o.id === item.value);
    if (selectedOption?.value !== undefined && selectedOption?.value !== null) {
      return String(selectedOption.value);
    }
  }

  if (item.type === 'range') {
    if (item.subType === 'time') {
      const val = item.value as { start?: string; end?: string };
      if (!val?.start && !val?.end) return null;
      return `${val?.start || '-'} - ${val?.end || '-'}`;
    }
    const val = item.value as { min?: number; max?: number };
    if (val?.min === undefined && val?.max === undefined) return null;
    return `${val?.min ?? '-'} - ${val?.max ?? '-'}`;
  }

  if (typeof item.value === 'string') {
    const trimmed = item.value.trim();
    if (!trimmed || trimmed === '-' || trimmed === '- - -') {
      return null;
    }
    return item.value;
  }

  if (typeof item.value === 'number') {
    return item.shouldFormat ? formatNumber(item.value) : String(item.value);
  }

  return null;
};

const InfoTable = ({ data, onEdit, onInlineEdit, onFieldAdd, columns = 1 }: InfoTableProps) => {
  const t = useTranslations('common');
  const [progress, setProgress] = useState<Progress>({});
  const [visibleEmptyFields, setVisibleEmptyFields] = useState<Set<string>>(new Set());

  const handleEmptyFieldEditingChange = (
    item: InfoTableItem,
    isEditing: boolean,
    currentValue?:
      | string
      | string[]
      | number
      | { start?: string; end?: string; min?: number; max?: number }
  ) => {
    if (isEditing || !isEmptyValue(currentValue as InfoTableItem['value'])) return;
    setVisibleEmptyFields(previous => {
      const next = new Set(previous);
      next.delete(item.key || item.name);
      return next;
    });
  };

  const clearProgress = useCallback((key: string) => {
    setProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[key];
      return newProgress;
    });
  }, []);

  const handleInlineEdit = async (
    key: string,
    value: string | string[] | Date | number | boolean | undefined | Record<string, any>
  ) => {
    console.log(key, value);
    if (onInlineEdit) {
      try {
        setProgress({ ...progress, [key]: { status: 'pending', message: '' } });
        const result = await onInlineEdit(key, value);
        if (result) {
          setProgress({ ...progress, [key]: { status: 'success', message: result } });
        }
      } catch (error) {
        console.error(error);
        setProgress({ ...progress, [key]: { status: 'error', message: getErrorMessage(error) } });
      } finally {
        setTimeout(() => {
          clearProgress(key);
        }, 3000);
      }
    }
  };

  const progressIcon = useCallback(
    (item: InfoTableItem) => {
      let key = item.key || item.name;
      let progressItem = progress[key];
      if (!progressItem && item.type === 'range') {
        key =
          item.subType === 'time'
            ? item.rangeKeys?.start || `${key}.start`
            : item.rangeKeys?.min || `${key}.min`;
        progressItem = progress[key];
        if (!progressItem) {
          key =
            item.subType === 'time'
              ? item.rangeKeys?.end || `${item.key}.end`
              : item.rangeKeys?.max || `${item.key}.max`;
          progressItem = progress[key];
        }
      }
      if (!progressItem) return null;

      return progressItem.status === 'pending' ? (
        <Icon name="loader-2-fill" size={4} aria-label="Loading" className="animate-spin w-4 h-4" />
      ) : progressItem.status === 'success' ? (
        <Icon name="check-circle-fill" size={4} className="text-success-base" />
      ) : (
        <Icon name="close-circle-fill" size={4} className="text-error-base" />
      );
    },
    [progress]
  );

  return (
    <table className="w-full max-w-full table-fixed">
      <tbody
        className={cn(
          'w-full min-w-0 max-w-full',
          columns === 2 ? 'grid grid-cols-2 gap-x-6' : 'block'
        )}
      >
        {data.map(item => {
          const copyableValue = getCopyableValue(item);
          return (
            <tr
              key={item.name}
              className={cn(
                'block w-full min-w-0 max-w-full',
                columns === 2 && item.columnSpan === 2 && 'col-span-2'
              )}
            >
              <td
                className="group block w-full min-w-0 max-w-full overflow-hidden py-3"
                valign="top"
              >
                <div className="group flex min-w-0 w-full max-w-full flex-col gap-1 items-start justify-start">
                  {item.showLabel !== false && (
                    <div className="flex items-center justify-between w-full min-w-0">
                      <div className="flex items-center gap-2 min-w-0 max-w-full overflow-hidden">
                        <h2 className="text-muted-foreground text-xs font-medium uppercase truncate">
                          {item.name}
                        </h2>
                        {item.labelSuffix && item.labelSuffix}
                        {copyableValue && <CopyButton value={copyableValue} />}
                        {item.showBadge && <Pill {...item.badgeProps}>{item.badgeLabel}</Pill>}
                        {progressIcon(item)}
                      </div>
                    </div>
                  )}

                  <div
                    className={cn(
                      'flex min-w-0 w-full max-w-full gap-2 items-center justify-start text-sm overflow-hidden',
                      item.type === 'textarea' && 'w-full'
                    )}
                  >
                    {item.prefix && item.prefix}
                    {!item.isInlineEdit ? (
                      <div className="w-full min-w-0 max-w-full truncate text-foreground text-sm">
                        {(() => {
                          if (item.type === 'number') {
                            return formatNumber(item.value as number) || '-';
                          }
                          if (item.type === 'range') {
                            if (item.subType === 'time') {
                              const val = item.value as { start?: string; end?: string };
                              if (!val?.start && !val?.end) return '-';
                              return `${val?.start || '-'} - ${val?.end || '-'}`;
                            }
                            const val = item.value as { min?: number; max?: number };
                            return `${val?.min ?? '-'} - ${val?.max ?? '-'}`;
                          }
                          if (item.type === 'tags') {
                            return (
                              <div className="flex flex-wrap gap-1">
                                {(item.value as string[])?.map((tag: string, index: number) => (
                                  <Pill
                                    color="neutral"
                                    className="rounded p-1 h-[unset] gap-1 text-sm"
                                    key={index}
                                  >
                                    {tag}
                                  </Pill>
                                ))}
                              </div>
                            );
                          }
                          if (item.type === 'select' || item.type === 'user-select') {
                            return item.options?.find(o => o.id === item.value)?.value || '-';
                          }
                          return (item.value as React.ReactNode) ?? '-';
                        })()}
                      </div>
                    ) : isEmptyValue(item.value) &&
                      !visibleEmptyFields.has(item.key || item.name) ? (
                      <button
                        type="button"
                        className="text-sm text-primary"
                        onClick={() => {
                          onFieldAdd?.(item);
                          setVisibleEmptyFields(previous => {
                            const next = new Set(previous);
                            next.add(item.key || item.name);
                            return next;
                          });
                        }}
                      >
                        + Add
                      </button>
                    ) : (
                      <>
                        {(!item.type || item.type === 'text') && (
                          <EditableText
                            isInfoTable
                            initialEditing={visibleEmptyFields.has(item.key || item.name)}
                            onEditingChange={(editing, value) =>
                              handleEmptyFieldEditingChange(item, editing, value)
                            }
                            className={item.itemWrapperClassName}
                            initialValue={item.value as string}
                            type="text"
                            onSave={value => handleInlineEdit(item.key || item.name, value)}
                            validation={item.validation}
                            placeholder={item.placeholder}
                          />
                        )}
                        {item.type === 'number' && (
                          <EditableText
                            isInfoTable
                            initialEditing={visibleEmptyFields.has(item.key || item.name)}
                            onEditingChange={(editing, value) =>
                              handleEmptyFieldEditingChange(item, editing, value)
                            }
                            className={item.itemWrapperClassName}
                            initialValue={item.value as number}
                            type="number"
                            onSave={value => handleInlineEdit(item.key || item.name, value)}
                            validation={item.validation}
                            placeholder={item.placeholder}
                            shouldFormat={item.shouldFormat}
                            prefixIcon={item.prefixIcon}
                            inputClassName={item.inputClassName}
                            prefixWrapperClassName={item.prefixWrapperClassName}
                          />
                        )}
                        {item.type === 'textarea' && (
                          <EditableText
                            isInfoTable
                            initialEditing={visibleEmptyFields.has(item.key || item.name)}
                            onEditingChange={(editing, value) =>
                              handleEmptyFieldEditingChange(item, editing, value)
                            }
                            className={item.itemWrapperClassName}
                            initialValue={item.value as string}
                            type="textarea"
                            onSave={value => handleInlineEdit(item.key || item.name, value)}
                            validation={item.validation}
                            characterLimit={item.characterLimit}
                            placeholder={item.placeholder}
                          />
                        )}
                        {item.type === 'tags' &&
                          (item.isInlineEdit ? (
                            <EditableText
                              isInfoTable
                              initialEditing={visibleEmptyFields.has(item.key || item.name)}
                              onEditingChange={(editing, value) =>
                                handleEmptyFieldEditingChange(item, editing, value)
                              }
                              className={item.itemWrapperClassName}
                              initialValue={item.value as string[]}
                              type="tags"
                              subType={item.subType as any}
                              onSave={value => handleInlineEdit(item.key || item.name, value)}
                              placeholder={item.placeholder}
                            />
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(item.value as string[])?.map((tag: string, index: number) => (
                                <Pill
                                  color="neutral"
                                  className="rounded p-1 h-[unset] gap-1 text-sm"
                                  key={index}
                                >
                                  {tag}
                                </Pill>
                              ))}
                            </div>
                          ))}
                        {item.type === 'select' &&
                          (item.isInlineEdit ? (
                            <EditableText
                              isInfoTable
                              className={item.itemWrapperClassName}
                              initialValue={item.value as string}
                              type="select"
                              multiple={item.multiple}
                              maxTags={item.maxTags}
                              options={item.options || []}
                              searchable={item.searchable}
                              onSave={v => {
                                handleInlineEdit(item.key || item.name, v);
                              }}
                            />
                          ) : (
                            <p className="text-foreground text-sm">
                              {item.options?.find(o => o.id === item.value)?.value || '-'}
                            </p>
                          ))}

                        {item.type === 'user-select' &&
                          (item.isInlineEdit ? (
                            <EditableText
                              isInfoTable
                              className={item.itemWrapperClassName}
                              initialValue={item.value as string}
                              type="user-select"
                              onSave={v => {
                                handleInlineEdit(item.key || item.name, v);
                              }}
                            />
                          ) : (
                            <p className="text-foreground text-sm">
                              {item.options?.find(o => o.id === item.value)?.value || '-'}
                            </p>
                          ))}

                        {item.type === 'date' && (
                          <EditableText
                            isInfoTable
                            className={item.itemWrapperClassName}
                            initialValue={item.value as string}
                            type="date"
                            subType={item.subType as 'date' | 'datetime'}
                            onSave={v => handleInlineEdit(item.key || item.name, v)}
                          />
                        )}

                        {item.type === 'range' &&
                          (item.subType === 'time' ? (
                            <TimeRangeControl
                              item={item}
                              initialEditing={visibleEmptyFields.has(item.key || item.name)}
                              onEmptyChange={(editing, val) =>
                                handleEmptyFieldEditingChange(item, editing, val)
                              }
                              onSave={values => {
                                handleInlineEdit(item.key || item.name, values);
                              }}
                            />
                          ) : (
                            <div className="flex gap-2 items-center justify-start">
                              <EditableText
                                isInfoTable
                                initialEditing={visibleEmptyFields.has(item.key || item.name)}
                                onEditingChange={(editing, val) =>
                                  handleEmptyFieldEditingChange(item, editing, {
                                    min: val as number,
                                    max: (item.value as { max: number })?.max,
                                  })
                                }
                                className={item.itemWrapperClassName}
                                inputClassName={item.inputClassName}
                                initialValue={(item.value as { min: number; max: number })?.min}
                                type="number"
                                placeholder="Min"
                                onSave={v =>
                                  handleInlineEdit(
                                    item.rangeKeys?.min || `${item.key}.min` || item.name,
                                    v
                                  )
                                }
                              />
                              <span className="text-foreground text-sm">-</span>
                              <EditableText
                                isInfoTable
                                onEditingChange={(editing, val) =>
                                  handleEmptyFieldEditingChange(item, editing, {
                                    min: (item.value as { min: number })?.min,
                                    max: val as number,
                                  })
                                }
                                className={item.itemWrapperClassName}
                                inputClassName={item.inputClassName}
                                initialValue={(item.value as { min: number; max: number })?.max}
                                type="number"
                                placeholder="Max"
                                onSave={v =>
                                  handleInlineEdit(
                                    item.rangeKeys?.max || `${item.key}.max` || item.name,
                                    v
                                  )
                                }
                              />
                            </div>
                          ))}

                        {item.type === 'link' && (
                          <Link href={item.value as string} className="text-foreground underline">
                            {item.value as string}
                          </Link>
                        )}
                        {item.type === 'checkbox' && (
                          <div className="flex items-center h-6">
                            <Checkbox
                              id={item.key || item.name}
                              checked={!!item.value}
                              onCheckedChange={checked =>
                                handleInlineEdit(item.key || item.name, checked)
                              }
                              disabled={!item.isInlineEdit}
                            />
                            <span className="text-sm text-foreground">
                              {item.value ? t('yes') : t('no')}
                            </span>
                          </div>
                        )}
                        {item.type === 'switch' && (
                          <div className="flex items-center h-6 gap-2">
                            <Switch
                              id={item.key || item.name}
                              checked={!!item.value}
                              onCheckedChange={checked =>
                                handleInlineEdit(item.key || item.name, checked)
                              }
                              disabled={!item.isInlineEdit}
                              size="sm"
                            />
                            <span className="text-sm text-foreground">
                              {item.value ? t('yes') : t('no')}
                            </span>
                          </div>
                        )}
                        {item.isEditable && !item.isInlineEdit && (
                          <Button
                            variant="link"
                            className="px-0 text-muted-foreground gap-0"
                            onClick={() => {
                              onEdit?.(item);
                            }}
                          >
                            <Icon name="pencil-line" size={5} />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default InfoTable;
